import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, IconButton, InputAdornment, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import {
    ContactlessRounded as ReaderIcon,
    ManageSearchRounded as LookupIcon,
    VolumeUpRounded as SoundOnIcon,
    VolumeOffRounded as SoundOffIcon,
    FullscreenRounded as FullscreenIcon,
    FullscreenExitRounded as FullscreenExitIcon,
    LogoutRounded as ExitIcon,
    FreeBreakfastRounded as BreakfastIcon,
    LunchDiningRounded as LunchIcon,
    DinnerDiningRounded as DinnerIcon,
    ReceiptLongRounded as TotalIcon,
    TaskAltRounded as DeliveredIcon,
    HourglassTopRounded as RemainingIcon,
    AutoModeRounded as AutoIcon,
    WarningAmberRounded as WarningIcon,
    TouchAppRounded as ActivateIcon,
} from '@mui/icons-material';
import { reservationsAPI, getImageUrl, systemSettingsAPI, MealType } from '../services/api';
import { toPersianNumber } from '../utils/persianNumber';
import { useAuth } from '../contexts/AuthContext';
import foodFlowLogo from '../assets/foodflow-w.png';
import packageJson from '../../package.json';
import { M, MEAL_LABELS, MEAL_ORDER, SCAN_KIND, ScanKind, formatClock, kindFromCode, localYmd, mealTypeFromClock } from '../components/monitor/monitorTokens';
import { KpiTile, LiveClock, StatusPill } from '../components/monitor/MonitorParts';
import MealProgressStrip, { MealBreakdown } from '../components/monitor/MealProgressStrip';
import ScanResultPanel, { ScanEntry, UndoState } from '../components/monitor/ScanResultPanel';
import QueuePanel, { DailyReservation, PendingPerson } from '../components/monitor/QueuePanel';
import ReservationLookupDrawer from '../components/monitor/ReservationLookupDrawer';
import { CardEnrollDialog, DeliveryConfirmDialog, UndoDeliveryDialog, UndoLine, UndoTarget } from '../components/monitor/MonitorDialogs';
import { useScanSound } from '../components/monitor/useScanSound';

interface DailyStats {
    total: number;
    delivered: number;
    remaining: number;
    mealBreakdown?: MealBreakdown[];
}

const REFRESH_MS = 20000;
const HISTORY_SIZE = 30;

const MEAL_ICONS: Record<MealType, React.ReactElement> = {
    breakfast: <BreakfastIcon />,
    lunch: <LunchIcon />,
    dinner: <DinnerIcon />,
};

// Older backends answer without `code`; infer the outcome from the payload instead
const kindFromErrorPayload = (data: any): ScanKind => {
    if (data?.code) return kindFromCode(data.code, false);
    if (data?.deliveredAt || String(data?.error || '').includes('قبلاً')) return 'already';
    if (data?.user) return 'noReservation';
    return 'notFound';
};

const isSuperAdminDomain = () => {
    const hostname = window.location.hostname;
    return hostname.startsWith('co.') || hostname.includes('superadmin');
};

const RestaurantMonitor: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const { soundEnabled, setSoundEnabled, playScanSound } = useScanSound();

    const [customLogo, setCustomLogo] = useState<string | null>(null);
    useEffect(() => {
        systemSettingsAPI.getPublicSettings(user?.organizationId)
            .then((settings: any) => { if (settings?.logoUrl) setCustomLogo(getImageUrl(settings.logoUrl)); })
            .catch((err: unknown) => console.error('Failed to fetch public settings', err));
    }, [user?.organizationId]);

    // ---- meal type: follows the clock until the operator picks one ----
    const [mealType, setMealType] = useState<MealType>(() => mealTypeFromClock());
    const [mealAuto, setMealAuto] = useState(true);
    const mealTypeRef = useRef(mealType);
    mealTypeRef.current = mealType;

    useEffect(() => {
        if (!mealAuto) return;
        const sync = () => setMealType(mealTypeFromClock());
        sync();
        const t = setInterval(sync, 30000);
        return () => clearInterval(t);
    }, [mealAuto]);

    const pickMeal = (mt: MealType) => {
        setMealAuto(false);
        setMealType(mt);
    };

    // ---- live data ----
    const [stats, setStats] = useState<DailyStats>({ total: 0, delivered: 0, remaining: 0, mealBreakdown: [] });
    const [reservations, setReservations] = useState<DailyReservation[]>([]);
    const [loadFailed, setLoadFailed] = useState(false);

    const refresh = useCallback(async (mt: MealType) => {
        const today = localYmd();
        try {
            const [s, r] = await Promise.all([
                reservationsAPI.getTodayStats(mt),
                reservationsAPI.getDailyReservations(today, today, mt),
            ]);
            if (mt !== mealTypeRef.current) return; // the operator switched meals meanwhile
            setStats(s);
            setReservations(r || []);
            setLoadFailed(false);
        } catch (err) {
            console.error('Failed to refresh monitor data', err);
            setLoadFailed(true);
        }
    }, []);

    useEffect(() => {
        refresh(mealType);
        const t = setInterval(() => refresh(mealType), REFRESH_MS);
        return () => clearInterval(t);
    }, [mealType, refresh]);

    // ---- dialogs / panels that take the keyboard away from the scan box ----
    const [lookupOpen, setLookupOpen] = useState(false);
    const [enrollSerial, setEnrollSerial] = useState<string | null>(null);
    const [confirmPerson, setConfirmPerson] = useState<PendingPerson | null>(null);
    const [undoTarget, setUndoTarget] = useState<UndoTarget | null>(null);
    const blocking = lookupOpen || enrollSerial !== null || confirmPerson !== null || undoTarget !== null;
    const blockingRef = useRef(blocking);
    blockingRef.current = blocking;

    // ---- scanning: a queue so fast consecutive card reads are never dropped ----
    const [scanText, setScanText] = useState('');
    const [current, setCurrent] = useState<ScanEntry | null>(null);
    const [history, setHistory] = useState<ScanEntry[]>([]);
    const [busy, setBusy] = useState(false);
    const queueRef = useRef<string[]>([]);
    const processingRef = useRef(false);
    const dismissRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // The result goes back to "ready" on its own so the next person never sees the previous
    // person's result. While the pointer rests on it the countdown pauses, so an operator
    // reaching for "undo" does not see the button vanish under the cursor.
    const dismissStateRef = useRef<{ id: string; remaining: number; startedAt: number } | null>(null);
    const hoverResultRef = useRef(false);
    const [resultHovered, setResultHovered] = useState(false);

    const runDismiss = useCallback(() => {
        const st = dismissStateRef.current;
        clearTimeout(dismissRef.current);
        if (!st || hoverResultRef.current) return;
        st.startedAt = Date.now();
        dismissRef.current = setTimeout(() => {
            dismissStateRef.current = null;
            setCurrent((c) => (c?.id === st.id ? null : c));
        }, st.remaining);
    }, []);

    const onResultHover = (hovering: boolean) => {
        hoverResultRef.current = hovering;
        setResultHovered(hovering);
        const st = dismissStateRef.current;
        if (!st) return;
        if (hovering) {
            clearTimeout(dismissRef.current);
            st.remaining = Math.max(800, st.remaining - (Date.now() - st.startedAt));
        } else {
            runDismiss();
        }
    };

    const showEntry = useCallback((entry: ScanEntry) => {
        clearTimeout(dismissRef.current);
        setCurrent(entry);
        setHistory((h) => [entry, ...h].slice(0, HISTORY_SIZE));
        playScanSound(entry.kind);
        const ms = SCAN_KIND[entry.kind].dismissMs;
        dismissStateRef.current = ms ? { id: entry.id, remaining: ms, startedAt: Date.now() } : null;
        runDismiss();
    }, [playScanSound, runDismiss]);

    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        processingRef.current = true;
        setBusy(true);
        try {
            while (queueRef.current.length > 0) {
                const identifier = queueRef.current.shift() as string;
                const mt = mealTypeRef.current;
                const base = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, at: new Date(), identifier, mealType: mt };
                clearTimeout(dismissRef.current);
                setCurrent(null);
                try {
                    const data = await reservationsAPI.scanCard(identifier, mt);
                    showEntry({
                        ...base,
                        kind: kindFromCode(data.code, true),
                        message: data.message,
                        isGroup: data.isGroup,
                        groupTitle: data.groupTitle,
                        user: data.user,
                        meals: data.meals,
                        reservationIds: data.reservationIds,
                        isGuestToken: data.isGuestToken,
                        description: data.description,
                    });
                } catch (err: any) {
                    const data = err.response?.data;
                    if (!err.response || err.response.status >= 500) {
                        showEntry({ ...base, kind: 'error', message: data?.error || 'network' });
                    } else if (data?.unknownCard && data?.cardSerial) {
                        showEntry({ ...base, kind: 'unknownCard', message: data.error });
                        setEnrollSerial(data.cardSerial);
                    } else {
                        showEntry({
                            ...base,
                            kind: kindFromErrorPayload(data),
                            message: data?.error || 'خطای ناشناخته',
                            user: data?.user,
                            meals: data?.meals,
                            deliveredAt: data?.deliveredAt,
                            isGroup: data?.isGroup,
                            groupTitle: data?.groupTitle,
                            isGuestToken: data?.isGuestToken,
                            description: data?.description,
                        });
                    }
                }
            }
        } finally {
            processingRef.current = false;
            setBusy(false);
            refresh(mealTypeRef.current);
        }
    }, [refresh, showEntry]);

    const enqueue = useCallback((value: string) => {
        const v = value.trim();
        if (!v) return;
        queueRef.current.push(v);
        processQueue();
    }, [processQueue]);

    useEffect(() => () => clearTimeout(dismissRef.current), []);

    // ---- keep the card reader's keystrokes landing in the scan box ----
    const scanInputRef = useRef<HTMLInputElement>(null);
    const [scanFocused, setScanFocused] = useState(false);

    // Safety net for focus lost some other way (e.g. the kiosk window regaining focus)
    useEffect(() => {
        const reclaim = () => {
            if (blockingRef.current) return;
            const el = document.activeElement as HTMLElement | null;
            if (el === scanInputRef.current) return;
            const typingElsewhere = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
            if (!typingElsewhere) scanInputRef.current?.focus({ preventScroll: true });
        };
        reclaim();
        const t = setInterval(reclaim, 1500);
        return () => clearInterval(t);
    }, []);

    // A mouse click anywhere but a text field must not move focus: otherwise a card read right
    // after clicking (say) a meal button is lost, and the reader's trailing Enter presses that
    // button again. Clicks still work; only the focus change is suppressed.
    const keepScanFocus = (e: React.MouseEvent) => {
        // dialogs and the lookup drawer render in portals but bubble here; they manage their own focus
        if (blockingRef.current) return;
        const target = e.target as HTMLElement;
        if (target.closest('input, textarea, [contenteditable="true"]')) return;
        e.preventDefault();
    };

    useEffect(() => {
        if (blocking) return;
        const t = setTimeout(() => scanInputRef.current?.focus({ preventScroll: true }), 80);
        return () => clearTimeout(t);
    }, [blocking]);

    // F2 opens / closes the reservation lookup (card readers never send function keys)
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'F2') {
                e.preventDefault();
                setLookupOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // ---- kiosk full screen ----
    const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement));
    useEffect(() => {
        const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const toggleFullscreen = () => {
        if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
        else document.documentElement.requestFullscreen?.().catch(() => undefined);
    };

    const closeLookup = useCallback(() => setLookupOpen(false), []);
    const pending = reservations.filter((r) => !r.isDelivered);
    const delivered = reservations.filter((r) => r.isDelivered);

    // ---- undo delivery ----
    // reservation id -> when its delivery was undone. Time-aware, so a reservation delivered again
    // after an undo is not shown as undone, and only its newest delivery offers undo.
    const [undoneAt, setUndoneAt] = useState<Map<string, number>>(() => new Map());
    const undoneAfter = useCallback((id: string, entry: ScanEntry) => (undoneAt.get(id) ?? 0) > entry.at.getTime(), [undoneAt]);

    const undoStateOf = useCallback((entry: ScanEntry): UndoState => {
        if (entry.kind !== 'delivered' || !entry.reservationIds?.length) return 'none';
        if (entry.reservationIds.every((id) => undoneAfter(id, entry))) return 'undone';
        const t = entry.at.getTime();
        const redeliveredLater = history.some((h) =>
            h.kind === 'delivered' && h.at.getTime() > t && h.reservationIds?.some((id) => entry.reservationIds!.includes(id)));
        return redeliveredLater ? 'none' : 'available';
    }, [history, undoneAfter]);

    const undoFromEntry = (entry: ScanEntry) => {
        const lines: UndoLine[] = (entry.meals || [])
            .map((m, i) => {
                const ids = (m.reservationIds || []).filter((id) => !undoneAfter(id, entry));
                return { key: `${m.title}-${i}`, title: m.title, imageUrl: m.imageUrl, quantity: ids.length, reservationIds: ids };
            })
            .filter((l) => l.quantity > 0);
        if (lines.length === 0) return;
        setUndoTarget({
            heading: entry.isGroup ? `گروه ${entry.groupTitle}` : entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'تحویل اسکن‌شده',
            subheading: `تحویل ساعت ${formatClock(entry.at)}`,
            lines,
        });
    };

    const undoFromPerson = (person: PendingPerson) => {
        setUndoTarget({
            heading: `${person.user.firstName} ${person.user.lastName}`,
            subheading: person.user.personnelCode && person.user.personnelCode !== '-' ? `کد پرسنلی ${toPersianNumber(person.user.personnelCode)}` : undefined,
            lines: person.reservations.map((r) => ({
                key: r.id, title: r.meal.title, imageUrl: r.meal.imageUrl, quantity: 1, deliveredAt: r.deliveredAt, reservationIds: [r.id],
            })),
        });
    };

    const confirmUndo = async (ids: string[], lines: UndoLine[]) => {
        const target = undoTarget;
        const result = await reservationsAPI.undoDelivery(ids); // errors are shown inside the dialog
        const done = new Set(result.reservationIds);
        const undoneTime = Date.now();
        setUndoneAt((prev) => {
            const next = new Map(prev);
            result.reservationIds.forEach((id) => next.set(id, undoneTime));
            return next;
        });
        setUndoTarget(null);
        showEntry({
            id: `undo-${Date.now()}`,
            at: new Date(),
            kind: 'undone',
            message: result.message,
            identifier: '',
            mealType: mealTypeRef.current,
            user: target ? { firstName: target.heading, lastName: '' } : undefined,
            meals: lines
                .map((l) => ({ title: l.title, imageUrl: l.imageUrl, quantity: l.reservationIds.filter((id) => done.has(id)).length }))
                .filter((m) => m.quantity > 0),
        });
        refresh(mealTypeRef.current);
    };
    const deliveredPct = stats.total ? (stats.delivered / stats.total) * 100 : 0;

    const iconButtonSx = {
        width: 48, height: 48, borderRadius: M.radiusControl, color: M.textMuted, border: `1px solid ${M.border}`,
        '&:hover': { bgcolor: M.surfaceHover, color: M.text },
        '&.Mui-focusVisible': { outline: `3px solid ${M.primary}`, outlineOffset: 2 },
    };

    return (
        <Box
            dir="rtl"
            onMouseDown={keepScanFocus}
            sx={{
                height: { xs: 'auto', md: '100vh' }, minHeight: '100vh', overflow: { xs: 'auto', md: 'hidden' },
                bgcolor: M.bg, color: M.text, fontFamily: M.font,
                display: 'flex', flexDirection: 'column', gap: 1.5, p: { xs: 1.5, md: 2 },
            }}
        >
            {/* ---------- top bar ---------- */}
            <Box
                component="header"
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                    bgcolor: M.surface, border: `1px solid ${M.border}`, borderRadius: M.radiusCard, boxShadow: M.cardShadow, px: 2, py: 1.25,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        component="img"
                        src={isSuperAdminDomain() ? foodFlowLogo : (customLogo || foodFlowLogo)}
                        alt="لوگوی سازمان"
                        sx={{ height: 48, width: 96, objectFit: 'contain' }}
                    />
                    <Box>
                        <Typography component="h1" sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '1.35rem', color: M.text, lineHeight: 1.3 }}>
                            مانیتور تحویل غذا
                        </Typography>
                        <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>سلف‌سرویس</Typography>
                    </Box>
                </Box>

                <Box role="radiogroup" aria-label="وعده‌ی غذایی" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, bgcolor: M.bg, borderRadius: '14px', border: `1px solid ${M.border}` }}>
                    {MEAL_ORDER.map((mt) => {
                        const active = mt === mealType;
                        return (
                            <ButtonBase
                                key={mt}
                                role="radio"
                                aria-checked={active}
                                onClick={() => pickMeal(mt)}
                                sx={{
                                    height: 48, px: 2.25, gap: 1, borderRadius: M.radiusControl, fontFamily: M.font, fontWeight: 800, fontSize: '1.05rem',
                                    color: active ? '#fff' : M.textMuted, bgcolor: active ? M.primaryStrong : 'transparent',
                                    transition: 'background-color 150ms, color 150ms',
                                    '&:hover': { bgcolor: active ? M.primaryStrong : M.surfaceHover, color: active ? '#fff' : M.text },
                                    '&.Mui-focusVisible': { outline: `3px solid ${M.primaryText}`, outlineOffset: 2 },
                                }}
                            >
                                {MEAL_ICONS[mt]}
                                {MEAL_LABELS[mt]}
                            </ButtonBase>
                        );
                    })}
                </Box>
                {mealAuto ? (
                    <Tooltip title="وعده بر اساس ساعت خودکار عوض می‌شود">
                        <Box><StatusPill tone="info" icon={<AutoIcon />}>خودکار</StatusPill></Box>
                    </Tooltip>
                ) : (
                    <Button
                        onClick={() => setMealAuto(true)}
                        startIcon={<AutoIcon />}
                        sx={{ height: 48, borderRadius: M.radiusControl, fontFamily: M.font, fontWeight: 700, color: M.infoText, '&:hover': { bgcolor: M.infoSoft } }}
                    >
                        بازگشت به خودکار
                    </Button>
                )}

                <Box sx={{ flex: 1 }} />

                <LiveClock />

                <Button
                    onClick={() => setLookupOpen(true)}
                    startIcon={<LookupIcon />}
                    aria-keyshortcuts="F2"
                    sx={{
                        height: 48, px: 2, borderRadius: M.radiusControl, fontFamily: M.font, fontWeight: 800, fontSize: '1rem',
                        color: M.infoText, bgcolor: M.infoSoft, border: `1px solid ${M.info}66`,
                        '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.24)' },
                    }}
                >
                    استعلام رزرو
                    <Box component="kbd" sx={{ ms: 1, px: 0.75, py: 0.1, borderRadius: '6px', border: `1px solid ${M.info}66`, fontSize: '0.75rem', fontFamily: 'inherit' }}>F2</Box>
                </Button>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={soundEnabled ? 'بی‌صدا کردن' : 'پخش صدای اسکن'}>
                        <IconButton onClick={() => setSoundEnabled(!soundEnabled)} aria-label={soundEnabled ? 'بی‌صدا کردن صدای اسکن' : 'فعال کردن صدای اسکن'} aria-pressed={soundEnabled} sx={iconButtonSx}>
                            {soundEnabled ? <SoundOnIcon /> : <SoundOffIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isFullscreen ? 'خروج از تمام‌صفحه' : 'تمام‌صفحه'}>
                        <IconButton onClick={toggleFullscreen} aria-label={isFullscreen ? 'خروج از حالت تمام‌صفحه' : 'نمایش تمام‌صفحه'} sx={iconButtonSx}>
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="خروج از مانیتور">
                        <IconButton onClick={() => navigate('/dashboard')} aria-label="خروج از مانیتور و بازگشت به داشبورد" sx={iconButtonSx}>
                            <ExitIcon sx={{ transform: 'scaleX(-1)' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ---------- counts ---------- */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: { xs: '1 1 100%', lg: '0 0 620px' } }}>
                    <KpiTile label={`رزرو ${MEAL_LABELS[mealType]}`} value={stats.total} tone="neutral" icon={<TotalIcon />} />
                    <KpiTile
                        label="تحویل شده"
                        value={stats.delivered}
                        tone="success"
                        icon={<DeliveredIcon />}
                        progress={deliveredPct}
                        caption={`${toPersianNumber(Math.round(deliveredPct))}٪`}
                    />
                    <KpiTile label="باقی‌مانده" value={stats.remaining} tone="warning" icon={<RemainingIcon />} />
                </Box>
                <MealProgressStrip items={stats.mealBreakdown || []} />
            </Box>

            {loadFailed && (
                <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: M.radiusControl, bgcolor: M.warningSoft, color: M.warningText, border: `1px solid ${M.warning}55` }}>
                    <WarningIcon fontSize="small" />
                    <Typography sx={{ fontFamily: M.font, fontSize: '0.95rem', color: 'inherit' }}>
                        به‌روزرسانی آمار انجام نشد؛ اعداد ممکن است قدیمی باشند. تلاش دوباره خودکار انجام می‌شود.
                    </Typography>
                </Box>
            )}

            {/* ---------- scan + queue ---------- */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gridTemplateRows: { xs: 'minmax(440px, auto) 520px', md: '1fr' }, gap: 1.5 }}>
                <Box
                    component="section"
                    aria-label="اسکن کارت و نتیجه‌ی تحویل"
                    sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: M.surface, border: `1px solid ${M.border}`, borderRadius: M.radiusCard, boxShadow: M.cardShadow, p: 2 }}
                >
                    <TextField
                        inputRef={scanInputRef}
                        value={scanText}
                        onChange={(e) => setScanText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                enqueue(scanText);
                                setScanText('');
                            }
                        }}
                        onFocus={() => setScanFocused(true)}
                        onBlur={(e) => {
                            setScanFocused(false);
                            // a click on empty space sends focus to <body>: take it back at once so no card
                            // read is lost; focus moving to a button, field or dialog is left alone
                            if (!e.relatedTarget && !blockingRef.current && document.hasFocus()) {
                                setTimeout(() => {
                                    if (!blockingRef.current && document.activeElement === document.body) {
                                        scanInputRef.current?.focus({ preventScroll: true });
                                    }
                                }, 0);
                            }
                        }}
                        autoFocus
                        autoComplete="off"
                        fullWidth
                        placeholder="اسکن کارت یا ورود کد پرسنلی + Enter"
                        inputProps={{ 'aria-label': 'اسکن کارت یا ورود کد پرسنلی برای تحویل غذا' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <ReaderIcon sx={{ color: scanFocused ? M.successText : M.textFaint }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: busy ? M.warning : scanFocused ? M.success : M.textFaint }} />
                                        <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: M.textMuted, whiteSpace: 'nowrap' }}>
                                            {busy ? 'در حال بررسی…' : scanFocused ? 'آماده‌ی اسکن' : 'غیرفعال'}
                                        </Typography>
                                    </Box>
                                </InputAdornment>
                            ),
                            sx: {
                                height: M.controlHeight, borderRadius: M.radiusControl, fontFamily: M.font, fontSize: '1.15rem', color: M.text, bgcolor: M.bg,
                                '& fieldset': { borderColor: M.borderStrong },
                                '&.Mui-focused fieldset': { borderColor: `${M.success} !important`, borderWidth: 2 },
                            },
                        }}
                    />

                    {!scanFocused && !blocking && (
                        <ButtonBase
                            onClick={() => scanInputRef.current?.focus()}
                            sx={{
                                justifyContent: 'flex-start', gap: 1, px: 2, minHeight: 48, borderRadius: M.radiusControl, textAlign: 'start',
                                bgcolor: M.warningSoft, color: M.warningText, border: `1px solid ${M.warning}55`, fontFamily: M.font, fontWeight: 700,
                            }}
                        >
                            <ActivateIcon />
                            کادر اسکن فعال نیست و کارت‌ها ثبت نمی‌شوند — برای فعال‌سازی اینجا را بزنید
                        </ButtonBase>
                    )}

                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }} onMouseEnter={() => onResultHover(true)} onMouseLeave={() => onResultHover(false)}>
                        <ScanResultPanel
                            entry={current}
                            busy={busy}
                            mealType={mealType}
                            reducedMotion={reducedMotion}
                            countdownPaused={resultHovered}
                            undoStateOf={undoStateOf}
                            onUndo={undoFromEntry}
                        />
                    </Box>
                </Box>

                <QueuePanel
                    pending={pending}
                    delivered={delivered}
                    totalReservations={stats.total}
                    history={history}
                    undoStateOf={undoStateOf}
                    onSelect={setConfirmPerson}
                    onUndoPerson={undoFromPerson}
                    onUndoEntry={undoFromEntry}
                />
            </Box>

            <Typography component="footer" sx={{ fontFamily: M.font, fontSize: '0.75rem', color: M.textFaint, textAlign: 'center' }}>
                FoodFlow · Badban Creative Studio · نسخه {toPersianNumber(packageJson.version)}
            </Typography>

            <DeliveryConfirmDialog
                person={confirmPerson}
                mealType={mealType}
                onCancel={() => setConfirmPerson(null)}
                onConfirm={(identifiers) => {
                    setConfirmPerson(null);
                    identifiers.forEach(enqueue);
                }}
            />
            <UndoDeliveryDialog target={undoTarget} onCancel={() => setUndoTarget(null)} onConfirm={confirmUndo} />
            <CardEnrollDialog
                serial={enrollSerial}
                onClose={() => {
                    setEnrollSerial(null);
                    setCurrent((c) => (c?.kind === 'unknownCard' ? null : c)); // back to "ready"
                }}
                onEnrolled={(serial) => {
                    setEnrollSerial(null);
                    enqueue(serial); // the card is known now: deliver right away
                }}
            />
            <ReservationLookupDrawer open={lookupOpen} onClose={closeLookup} mealType={mealType} />
        </Box>
    );
};

export default RestaurantMonitor;
